import api from './api';

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

class DocumentStorageService {
  constructor() {
    this.baseUrl = '/documents';
  }

  normalizeOptions(options) {
    if (typeof options === 'string') {
      return { companyId: options };
    }

    if (options && typeof options === 'object') {
      return { ...options };
    }

    return {};
  }

  buildConfig(companyId, config = {}) {
    const finalConfig = { ...(config || {}) };
    const headers = { ...(config && config.headers ? config.headers : {}) };

    if (companyId) {
      headers['x-company-id'] = companyId;
    }

    if (Object.keys(headers).length > 0) {
      finalConfig.headers = headers;
    }

    return finalConfig;
  }

  buildRequestConfig(options = {}, overrides = {}) {
    const normalizedOptions = this.normalizeOptions(options);
    const {
      companyId,
      user,
      userId,
      userEmail,
      userFirstName,
      userLastName,
      userName,
      ...config
    } = normalizedOptions;

    const baseHeaders = { ...(config.headers || {}) };
    const overrideHeaders = { ...(overrides.headers || {}) };

    const resolvedUser = user && typeof user === 'object' ? user : null;
    const resolvedUid = resolvedUser?.uid || resolvedUser?.id || userId || null;
    const resolvedEmail = resolvedUser?.email || userEmail || null;
    const resolvedFirstName = resolvedUser?.firstName || userFirstName || null;
    const resolvedLastName = resolvedUser?.lastName || userLastName || null;
    const resolvedName = resolvedUser?.name
      || resolvedUser?.displayName
      || userName
      || [resolvedFirstName, resolvedLastName].filter(Boolean).join(' ') || null;

    const headers = {
      ...baseHeaders,
      ...overrideHeaders
    };

    if (resolvedUid) {
      headers['x-user-id'] = resolvedUid;
    }
    if (resolvedEmail) {
      headers['x-user-email'] = resolvedEmail;
    }
    if (resolvedFirstName) {
      headers['x-user-first-name'] = resolvedFirstName;
    }
    if (resolvedLastName) {
      headers['x-user-last-name'] = resolvedLastName;
    }
    if (resolvedName) {
      headers['x-user-name'] = resolvedName;
    }

    const mergedConfig = {
      ...config,
      ...overrides,
      headers
    };

    if (config.params || overrides.params) {
      mergedConfig.params = {
        ...(config.params || {}),
        ...(overrides.params || {})
      };
    }

    return this.buildConfig(companyId, mergedConfig);
  }

  async getDocuments(filters = {}, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options, { params: filters });
      const response = await api.get(this.baseUrl, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getDocumentById(documentId, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.get(`${this.baseUrl}/${documentId}`, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createDocument(documentData, file = null, options = {}) {
    try {
      const normalizedOptions = this.normalizeOptions(options);
      const formData = new FormData();
      let hasCompanyId = false;

      if (file) {
        formData.append('document', file);
      }

      Object.entries(documentData || {}).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }

        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }

        if (key === 'companyId') {
          hasCompanyId = true;
        }
      });

      if (normalizedOptions.companyId && !hasCompanyId) {
        formData.append('companyId', normalizedOptions.companyId);
      }

      const requestConfig = this.buildRequestConfig(normalizedOptions);
      const response = await api.upload(this.baseUrl, formData, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateDocument(documentId, updateData, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.put(`${this.baseUrl}/${documentId}`, updateData, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteDocument(documentId, options = {}) {
    try {
      const requestOptions = typeof options === 'boolean' ? {} : options;
      const requestConfig = this.buildRequestConfig(requestOptions);
      const response = await api.delete(`${this.baseUrl}/${documentId}`, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async uploadDocumentFile(documentId, file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('document', file);

      const requestConfig = this.buildRequestConfig(options);
      const response = await api.upload(`${this.baseUrl}/${documentId}/upload`, formData, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getDownloadUrl(documentId, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.get(`${this.baseUrl}/${documentId}/download`, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async downloadDocument(documentId, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options, {
        responseType: 'blob',
        params: {
          ...(options?.params || {}),
          binary: true
        }
      });
      const response = await api.download(`${this.baseUrl}/${documentId}/download`, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async verifyDocument(documentId, notes = '', options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.put(`${this.baseUrl}/${documentId}/verify`, { notes }, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getExpiringDocuments(daysThreshold = 30, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options, {
        params: { daysThreshold }
      });
      const response = await api.get(`${this.baseUrl}/expiring/list`, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getEntityDocuments(entityType, entityId, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.get(`${this.baseUrl}/entity/${entityType}/${entityId}`, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async bulkUpdateDocuments(documentIds, updateData, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const payload = { documentIds, updateData };
      const response = await api.post(`${this.baseUrl}/bulk/update`, payload, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateExpirationStatuses(options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.post(`${this.baseUrl}/maintenance/update-expiration-statuses`, {}, requestConfig);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getDriverDocuments(driverId, filters = {}, options = {}) {
    try {
      const normalizedOptions = this.normalizeOptions(options);
      const mergedOptions = {
        ...normalizedOptions,
        params: {
          ...(normalizedOptions.params || {}),
          ...filters
        }
      };
      const response = await this.getEntityDocuments('driver', driverId, mergedOptions);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getVehicleDocuments(vehicleId, filters = {}, options = {}) {
    try {
      const normalizedOptions = this.normalizeOptions(options);
      const mergedOptions = {
        ...normalizedOptions,
        params: {
          ...(normalizedOptions.params || {}),
          ...filters
        }
      };
      const response = await this.getEntityDocuments('vehicle', vehicleId, mergedOptions);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getCompanyDocuments(filters = {}, options = {}) {
    try {
      const normalizedOptions = this.normalizeOptions(options);
      const mergedOptions = {
        ...normalizedOptions,
        params: {
          ...(normalizedOptions.params || {}),
          ...filters,
          category: 'company'
        }
      };
      const response = await this.getDocuments({}, mergedOptions);
      return response;
    } catch (error) {
      throw error;
    }
  }

  getDocumentTypes() {
    return {
      driver: [
        { value: 'cdl_license', label: 'CDL License', category: 'license' },
        { value: 'medical_certificate', label: 'Medical Certificate', category: 'medical' },
        { value: 'driver_application', label: 'Driver Application', category: 'employment' },
        { value: 'employment_verification', label: 'Employment Verification', category: 'employment' },
        { value: 'drug_test_results', label: 'Drug Test Results', category: 'medical' },
        { value: 'alcohol_test_results', label: 'Alcohol Test Results', category: 'medical' },
        { value: 'background_check', label: 'Background Check', category: 'background' },
        { value: 'driver_qualification_file', label: 'Driver Qualification File', category: 'compliance' },
        { value: 'annual_review', label: 'Annual Review', category: 'employment' },
        { value: 'training_certificate', label: 'Training Certificate', category: 'training' },
        { value: 'hazmat_endorsement', label: 'Hazmat Endorsement', category: 'license' },
        { value: 'mvr_license_check', label: "MVR Driver's License Check", category: 'compliance' }
      ],
      vehicle: [
        { value: 'vehicle_registration', label: 'Vehicle Registration', category: 'registration' },
        { value: 'vehicle_title', label: 'Vehicle Title', category: 'registration' },
        { value: 'annual_inspection', label: 'Annual Inspection', category: 'inspection' },
        { value: 'emissions_certificate', label: 'Emissions Certificate', category: 'inspection' },
        { value: 'ifta_permit', label: 'IFTA Permit', category: 'permit' },
        { value: 'apportioned_plates', label: 'Apportioned Plates', category: 'permit' },
        { value: 'fuel_permit', label: 'Fuel Permit', category: 'permit' },
        { value: 'operating_authority', label: 'Operating Authority', category: 'permit' },
        { value: 'vehicle_maintenance_record', label: 'Maintenance Record', category: 'maintenance' },
        { value: 'repair_invoice', label: 'Repair Invoice', category: 'maintenance' },
        { value: 'tire_inspection', label: 'Tire Inspection', category: 'inspection' },
        { value: 'brake_inspection', label: 'Brake Inspection', category: 'inspection' }
      ],
      company: [
        { value: 'insurance_certificate', label: 'Insurance Certificate', category: 'insurance' },
        { value: 'cargo_insurance', label: 'Cargo Insurance', category: 'insurance' },
        { value: 'liability_insurance', label: 'Liability Insurance', category: 'insurance' },
        { value: 'workers_comp', label: 'Workers Compensation', category: 'insurance' },
        { value: 'business_license', label: 'Business License', category: 'license' },
        { value: 'dot_authority', label: 'DOT Authority', category: 'permit' },
        { value: 'mc_authority', label: 'MC Authority', category: 'permit' },
        { value: 'usdot_registration', label: 'USDOT Registration', category: 'registration' },
        { value: 'drug_testing_policy', label: 'Drug Testing Policy', category: 'policy' },
        { value: 'safety_management_certificate', label: 'Safety Management Certificate', category: 'safety' },
        { value: 'hazmat_permit', label: 'Hazmat Permit', category: 'permit' }
      ]
    };
  }

  getAllowedFileTypes() {
    return [...ALLOWED_FILE_TYPES];
  }

  validateFile(file) {
    const parsedSize = Number.parseInt(process.env.REACT_APP_MAX_FILE_SIZE, 10);
    const maxSize = Number.isFinite(parsedSize) ? parsedSize : DEFAULT_MAX_FILE_SIZE;
    const allowedTypes = this.getAllowedFileTypes();

    if (!file) {
      throw new Error('No file provided');
    }

    if (file.size > maxSize) {
      throw new Error(`File size exceeds maximum limit of ${Math.round(maxSize / 1024 / 1024)}MB`);
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not allowed. Please upload PDF, image, or Office document files.');
    }

    return true;
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  getStatusColor(status) {
    const statusColors = {
      active: 'green',
      expired: 'red',
      pending_renewal: 'yellow',
      archived: 'gray'
    };
    return statusColors[status] || 'gray';
  }

  getDaysUntilExpiration(expirationDate) {
    if (!expirationDate) return null;

    const expDate = new Date(expirationDate);
    const today = new Date();
    const diffTime = expDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  isExpiringSoon(expirationDate, daysThreshold = 30) {
    const daysUntilExpiration = this.getDaysUntilExpiration(expirationDate);
    return daysUntilExpiration !== null && daysUntilExpiration <= daysThreshold && daysUntilExpiration > 0;
  }

  isExpired(expirationDate) {
    const daysUntilExpiration = this.getDaysUntilExpiration(expirationDate);
    return daysUntilExpiration !== null && daysUntilExpiration < 0;
  }

  canOCRFile(file) {
    if (!file) {
      return false;
    }

    const mimeType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
    const fileName = typeof file.name === 'string' ? file.name.toLowerCase() : '';

    const supportedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/tiff',
      'image/gif',
      'image/bmp',
      'image/webp'
    ];

    if (mimeType) {
      if (supportedMimeTypes.includes(mimeType)) {
        return true;
      }

      if (mimeType.startsWith('image/')) {
        return true;
      }
    }

    if (fileName) {
      return /(\.pdf|\.jpe?g|\.png|\.gif|\.bmp|\.tiff?|\.webp)$/i.test(fileName);
    }

    return false;
  }

  async uploadDocument(arg1, arg2 = null, arg3 = {}) {
    let documentData = arg1;
    let file = arg2;
    let options = arg3;

    const isFileInstance = (value) => {
      if (!value) {
        return false;
      }

      const fileCtor = typeof File !== 'undefined' ? File : null;
      const blobCtor = typeof Blob !== 'undefined' ? Blob : null;

      return (fileCtor && value instanceof fileCtor) || (blobCtor && value instanceof blobCtor);
    };

    if (isFileInstance(arg1)) {
      file = arg1;
      documentData = arg2 || {};
      options = arg3 || {};
    }

    const response = await this.createDocument(documentData || {}, file, options);

    return {
      success: true,
      document: response?.document || response,
      data: response
    };
  }

  async uploadNewVersion(documentId, file, metadata = {}, options = {}) {
    if (!file) {
      return {
        success: false,
        error: 'No file provided'
      };
    }

    const formData = new FormData();
    formData.append('document', file);

    Object.entries(metadata || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.upload(`${this.baseUrl}/${documentId}/upload`, formData, requestConfig);

      return {
        success: true,
        document: response?.document || response,
        data: response
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Failed to upload new version',
        details: error
      };
    }
  }

  async processOCR(documentId, payload = {}, options = {}) {
    try {
      const requestConfig = this.buildRequestConfig(options);
      const response = await api.post(`${this.baseUrl}/${documentId}/ocr`, payload, requestConfig);

      return {
        success: true,
        ocrData: response?.ocrData || response?.data || response,
        data: response
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'OCR processing failed',
        details: error
      };
    }
  }

  async scanDocument() {
    return {
      success: false,
      error: 'Live document scanning is not supported in this environment.'
    };
  }
}

const documentStorageService = new DocumentStorageService();
export default documentStorageService;
